// Year At A Glance — 52-week heat-strip.
//
// Goal: users spot tight or short weeks LONG before they arrive, so they can
// slow-play or reschedule bills before the crunch hits. Each vertical column
// is a week; color = status (green/amber/red). Month labels sit above the
// strip so the eye lands on "November" first and then narrows to weeks.
//
// Data comes from GET /api/forecast/year (see backend/forecast.py
// compute_year_view). The endpoint returns 52 week buckets + per-month
// rollups + starting balance + counts.

import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { api, formatMoney, formatDate } from "@/lib/api";
import { AlertTriangle, TriangleAlert, CheckCircle2, Info, ChevronRight } from "lucide-react";

const STATUS_STYLE = {
  safe: { bg: "bg-[#B8CFB1]", text: "text-[#2F4E32]", label: "Safe" },
  tight: { bg: "bg-[#E9C97A]", text: "text-[#5C4218]", label: "Tight" },
  short: { bg: "bg-[#D89A8F]", text: "text-[#7A2E24]", label: "Short" },
};

function StatusPill({ status, size = "sm" }) {
  const s = STATUS_STYLE[status] || STATUS_STYLE.safe;
  const dim = size === "sm" ? "text-[10px] px-2 py-0.5" : "text-xs px-2.5 py-1";
  return (
    <span className={`inline-flex items-center gap-1 rounded-full font-semibold uppercase tracking-wider ${dim} ${s.bg} ${s.text}`}>
      <span className="w-1.5 h-1.5 rounded-full bg-current opacity-70" /> {s.label}
    </span>
  );
}

export default function YearGlance() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hoverIdx, setHoverIdx] = useState(null);
  // Persisted pinned-week index. Users often reference the same week
  // repeatedly (e.g. planning around a tight month) so remembering the
  // pinned choice across refreshes cuts the "click again" friction.
  const [pinnedIdx, setPinnedIdx] = useState(() => {
    try {
      const v = window.localStorage.getItem("wmrb_year_pinned_week");
      if (v === null || v === "") return null;
      const n = Number(v);
      return Number.isFinite(n) && n >= 0 ? n : null;
    } catch { return null; }
  });

  // Sync pinned choice → localStorage. Clearing sets the key empty.
  useEffect(() => {
    try {
      if (pinnedIdx === null) window.localStorage.removeItem("wmrb_year_pinned_week");
      else window.localStorage.setItem("wmrb_year_pinned_week", String(pinnedIdx));
    } catch { /* ignore quota / private mode */ }
  }, [pinnedIdx]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/forecast/year?weeks=52");
        setData(data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Group weeks by primary month for the top axis.
  const byMonth = useMemo(() => {
    if (!data) return [];
    const groups = [];
    for (const wk of data.weeks) {
      const last = groups[groups.length - 1];
      if (last && last.month === wk.primary_month) {
        last.weeks.push(wk);
      } else {
        groups.push({
          month: wk.primary_month,
          label: wk.primary_month_label,
          weeks: [wk],
        });
      }
    }
    return groups;
  }, [data]);

  const detailIdx = pinnedIdx ?? hoverIdx;
  // Safety net: persisted pinnedIdx from a previous session might exceed
  // the returned weeks array (e.g. user changed cushion, weeks param).
  const detail = data && detailIdx != null && detailIdx < data.weeks.length ? data.weeks[detailIdx] : null;

  if (loading) return <div className="text-[#59605C]" data-testid="year-loading">Loading your year…</div>;
  if (!data) return <div className="text-[#59605C]">Couldn&apos;t load the year forecast.</div>;

  const nothingScheduled = data.weeks.every((w) => w.expected_income === 0 && w.bills_due === 0);

  return (
    <div className="space-y-8" data-testid="year-page">
      {/* Hero */}
      <div>
        <div className="text-xs font-semibold uppercase tracking-widest text-[#8A918D]">Year at a glance</div>
        <h1 className="font-heading text-3xl md:text-4xl font-semibold text-[#2C302E]">
          Spot the tight weeks — long before they arrive.
        </h1>
        <p className="mt-3 text-[15px] text-[#3E4340] leading-relaxed max-w-2xl">
          Each column is a week. Green = safe (cushion covered), amber = tight (within your cushion), red = short (projected negative or below your minimum). Hover a column for details, or click to pin.
        </p>
      </div>

      {/* Summary strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="year-summary">
        <SummaryTile
          label="Starting balance"
          value={formatMoney(data.starting_balance)}
          sub={data.past_due_total > 0 ? `After past-due: ${formatMoney(data.starting_balance - data.past_due_total)}` : null}
          accent="#2F4E32"
          testId="year-summary-starting"
        />
        <SummaryTile label="Past-due unpaid" value={data.past_due_total > 0 ? `-${formatMoney(data.past_due_total)}` : formatMoney(0)} accent={data.past_due_total > 0 ? "#8C3A30" : "#2F4E32"} testId="year-summary-pastdue" />
        <SummaryTile label="Tight weeks (52)" value={String(data.tight_week_count)} accent="#8A6B1F" testId="year-summary-tight" />
        <SummaryTile label="Short weeks (52)" value={String(data.short_week_count)} accent="#8C3A30" testId="year-summary-short" />
      </div>

      {/* Legend */}
      <div className="flex items-center flex-wrap gap-4 text-xs text-[#59605C]" data-testid="year-legend">
        <span className="flex items-center gap-2"><span className="w-3 h-3 rounded-sm bg-[#B8CFB1]" /> Safe — cushion covered</span>
        <span className="flex items-center gap-2"><span className="w-3 h-3 rounded-sm bg-[#E9C97A]" /> Tight — inside cushion</span>
        <span className="flex items-center gap-2"><span className="w-3 h-3 rounded-sm bg-[#D89A8F]" /> Short — below zero or cushion floor</span>
      </div>

      {nothingScheduled && (
        <div className="card-soft p-4 bg-[#FFFBF0] border border-[#E5D9BA] text-sm text-[#5C4218] flex items-start gap-2" data-testid="year-empty-hint">
          <Info size={16} className="mt-0.5 shrink-0" />
          <span>You haven&apos;t added recurring income or bills yet — the strip will fill in as soon as you do. <Link to="/app/dashboard" className="underline decoration-dotted underline-offset-2 font-semibold">Go to dashboard →</Link></span>
        </div>
      )}

      {/* Heat strip — horizontal scroll on narrow screens */}
      <div className="card-soft p-4 sm:p-6" data-testid="year-heatstrip-card">
        <div className="overflow-x-auto">
          <div className="min-w-[900px]">
            {/* Month labels above */}
            <div className="flex gap-[6px] items-end mb-2" data-testid="year-months-axis">
              {byMonth.map((g, i) => (
                <div key={i} style={{ width: `${g.weeks.length * 24 + (g.weeks.length - 1) * 6}px` }} className="text-center">
                  <div className="text-[10px] font-semibold uppercase tracking-widest text-[#59605C] truncate">
                    {g.label}
                  </div>
                  <div className="mt-1 h-px bg-[#EBE6E0]" />
                </div>
              ))}
            </div>

            {/* Week columns */}
            <div className="flex gap-[6px] items-end" data-testid="year-weeks-row">
              {data.weeks.map((wk, i) => {
                const s = STATUS_STYLE[wk.status];
                const isActive = detailIdx === i;
                // Column height reflects income − bills magnitude (visual heft)
                // with a floor so tiny values stay visible.
                const net = Math.abs(wk.expected_income - wk.bills_due);
                const height = Math.max(28, Math.min(90, 28 + net * 0.02));
                return (
                  <button
                    key={i}
                    type="button"
                    onMouseEnter={() => setHoverIdx(i)}
                    onMouseLeave={() => setHoverIdx(null)}
                    onClick={() => setPinnedIdx((p) => (p === i ? null : i))}
                    style={{ height: `${height}px`, width: "24px" }}
                    className={`rounded-sm ${s.bg} transition-all ${isActive ? "outline outline-2 outline-[#2C302E] scale-y-105" : "hover:brightness-110"}`}
                    aria-label={`Week ${i + 1}: ${wk.week_start} to ${wk.week_end}, ${s.label}, end balance ${wk.end_balance}`}
                    title={`Week ${i + 1} · ${formatDate(wk.week_start)} → ${formatDate(wk.week_end)} · ${s.label} · Ends ${formatMoney(wk.end_balance)}`}
                    data-testid={`year-week-${i}`}
                    data-status={wk.status}
                  />
                );
              })}
            </div>

            {/* Week numbers below (every 4th) */}
            <div className="flex gap-[6px] mt-2" data-testid="year-week-numbers">
              {data.weeks.map((_, i) => (
                <div key={i} style={{ width: "24px" }} className="text-center text-[9px] text-[#8A918D]">
                  {i % 4 === 0 ? `W${i + 1}` : ""}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Detail panel — reflects hover OR pinned week */}
        <div className="mt-6 border-t border-[#EBE6E0] pt-4">
          {detail ? (
            <WeekDetail week={detail} pinned={pinnedIdx === detailIdx} onUnpin={() => setPinnedIdx(null)} />
          ) : (
            <p className="text-xs text-[#8A918D] italic" data-testid="year-hover-hint">Hover a week to see its detail. Click to pin.</p>
          )}
        </div>
      </div>

      {/* Per-month rollup table */}
      <div className="card-soft p-6" data-testid="year-months-card">
        <div className="flex items-baseline justify-between mb-4">
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-widest text-[#8A918D]">Rolled up</div>
            <h2 className="font-heading text-xl font-medium text-[#2C302E]">By month</h2>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="year-months-table">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-widest text-[#8A918D] border-b border-[#EBE6E0]">
                <th className="py-2 pr-2">Month</th>
                <th className="py-2 pr-2">Worst week</th>
                <th className="py-2 pr-2 text-right">Income</th>
                <th className="py-2 pr-2 text-right">Bills</th>
                <th className="py-2 pr-2 text-right">Net</th>
              </tr>
            </thead>
            <tbody>
              {data.months.map((m) => {
                const net = m.expected_income - m.bills_due;
                return (
                  <tr key={m.month} className="border-b border-[#F5F1EC]" data-testid={`year-month-row-${m.month}`}>
                    <td className="py-2 pr-2 font-semibold text-[#2C302E]">
                      <Link to={`/app/planning?month=${m.month}-01`} className="hover:underline decoration-dotted underline-offset-2 inline-flex items-center gap-1">
                        {m.label} <ChevronRight size={12} className="text-[#8A918D]" />
                      </Link>
                    </td>
                    <td className="py-2 pr-2"><StatusPill status={m.worst_status} /></td>
                    <td className="py-2 pr-2 text-right text-[#3B5B3E]">+{formatMoney(m.expected_income)}</td>
                    <td className="py-2 pr-2 text-right text-[#8C3A30]">-{formatMoney(m.bills_due)}</td>
                    <td className={`py-2 pr-2 text-right font-semibold ${net < 0 ? "text-[#8C3A30]" : "text-[#3B5B3E]"}`}>
                      {net < 0 ? "-" : "+"}{formatMoney(Math.abs(net))}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Bottom advice card — small nudge based on the counts */}
      {(data.tight_week_count > 0 || data.short_week_count > 0) && (
        <div className="card-soft p-4 bg-[#FEF3EE] border border-[#F0C8B8] text-sm text-[#7A2E24] flex items-start gap-3" data-testid="year-advice">
          {data.short_week_count > 0 ? <AlertTriangle size={18} className="mt-0.5 shrink-0" /> : <TriangleAlert size={18} className="mt-0.5 shrink-0" />}
          <div>
            {data.short_week_count > 0 ? (
              <>
                <div className="font-semibold">{data.short_week_count} week{data.short_week_count === 1 ? "" : "s"} project short over the next 52 weeks.</div>
                <p className="mt-1 text-[13px] text-[#5C2A21]">Reschedule bills, add income sources, or set a small savings buffer. Every dollar moved earlier or later matters — the strip updates instantly.</p>
              </>
            ) : (
              <>
                <div className="font-semibold">{data.tight_week_count} week{data.tight_week_count === 1 ? "" : "s"} project tight — inside your cushion.</div>
                <p className="mt-1 text-[13px] text-[#5C2A21]">Not danger, but worth a look. Consider spreading a big bill or timing a paycheck to smooth them out.</p>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function SummaryTile({ label, value, sub, accent, testId }) {
  return (
    <div className="card-soft p-4" data-testid={testId}>
      <div className="text-[10px] font-semibold uppercase tracking-widest text-[#8A918D]">{label}</div>
      <div className="font-heading text-2xl font-semibold mt-1" style={{ color: accent }}>{value}</div>
      {sub && <div className="text-[11px] text-[#8A918D] mt-1">{sub}</div>}
    </div>
  );
}

function WeekDetail({ week, pinned, onUnpin }) {
  const s = STATUS_STYLE[week.status];
  return (
    <div data-testid="year-week-detail">
      <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-widest text-[#8A918D]">Week {week.index + 1}</div>
          <div className="font-heading text-lg font-medium text-[#2C302E]" data-testid="year-detail-window">
            {formatDate(week.week_start)} → {formatDate(week.week_end)}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <StatusPill status={week.status} size="md" />
          {pinned && (
            <button type="button" onClick={onUnpin} className="btn-ghost text-xs" data-testid="year-detail-unpin">Unpin</button>
          )}
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-2">
        <MiniStat label="Start" value={formatMoney(week.start_balance)} />
        <MiniStat label="+ Income" value={`+${formatMoney(week.expected_income)}`} pos />
        <MiniStat label="- Bills" value={`-${formatMoney(week.bills_due)}`} neg />
        <MiniStat label="End" value={formatMoney(week.end_balance)} big neg={week.end_balance < 0} pos={week.end_balance >= 0} />
      </div>
      {(week.paycheck_dates?.length > 0 || week.bill_count > 0) && (
        <div className="mt-3 text-xs text-[#59605C] space-y-1" data-testid="year-detail-events">
          {week.paycheck_dates.map((p, i) => (
            <div key={`p-${i}`} className="flex justify-between">
              <span>{p.source} · {formatDate(p.date)}</span>
              <span className="text-[#3B5B3E]">+{formatMoney(p.amount)}</span>
            </div>
          ))}
          {week.bill_count > 0 && (
            <div className="flex justify-between text-[#7A2E24]">
              <span>{week.bill_count} bill{week.bill_count === 1 ? "" : "s"} due this week</span>
              <span>-{formatMoney(week.bills_due)}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function MiniStat({ label, value, pos, neg, big }) {
  const color = pos ? "text-[#3B5B3E]" : neg ? "text-[#8C3A30]" : "text-[#2C302E]";
  return (
    <div className="p-2 rounded-lg bg-[#F9F8F6]">
      <div className="text-[10px] font-semibold uppercase tracking-widest text-[#8A918D]">{label}</div>
      <div className={`font-semibold ${big ? "text-lg" : "text-sm"} mt-0.5 ${color}`}>{value}</div>
    </div>
  );
}
